import {
  Component,
  inject,
  signal,
  computed,
  effect,
  OnDestroy,
} from '@angular/core'
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { forkJoin, Subscription } from 'rxjs'

import {
  PivotRecord,
  GeneratedContent,
  Enums,
} from '@cyborg-monorepo/hunter/common'
import {
  BaseDialogComponent,
  ButtonComponent,
  TypographyComponent,
  ErrorComponent,
  BreadcrumbListComponent,
  InputComponent,
  SearchInputComponent,
  ReusableDialogComponent,
  CardComponent,
  IconComponent,
  AccordionComponent,
} from '@hyperkit'

import {
  NavigationService,
  PivotAPIService,
  QueryViewService,
  QueryEditorService,
  PackageViewService,
  PackageEditorService,
  BreadcrumbService,
  SessionService,
  PivotFormStateService,
} from '@hunt/services'

import { PivotCardComponent } from './pivot-card/pivot-card.component'
export type PivotItemSource = 'auto' | 'demo' | 'recommended'
export interface PivotItem {
  UUID: string // match PivotRecord
  title: string
  description: string
  source?: PivotItemSource
}

@Component({
  standalone: true,
  selector: 'capture-data-dialog',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    TypographyComponent,
    ErrorComponent,
    BreadcrumbListComponent,
    PivotCardComponent,
    InputComponent,
    IconComponent,
    AccordionComponent,
    SearchInputComponent,
    ReusableDialogComponent,
    CardComponent,
  ],
  templateUrl: './capture-data-dialog.component.html',
  styleUrls: ['./capture-data-dialog.component.scss'],
})
export class CaptureDataDialogComponent
  extends BaseDialogComponent<boolean>
  implements OnDestroy
{
  private nav = inject(NavigationService)
  private pivotAPIService = inject(PivotAPIService)
  private queryView = inject(QueryViewService)
  private queryEditor = inject(QueryEditorService)
  private packageView = inject(PackageViewService)
  private packageEditor = inject(PackageEditorService)
  private pivotFormState = inject(PivotFormStateService)

  private breadcrumb = inject(BreadcrumbService)
  readonly session = inject(SessionService)

  private formValuesSub: Subscription | null = null
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )

  /** User-facing error message */
  public error = signal<string>('')

  /** Search term */
  public pivotSearchTerm = signal('')

  /** Track selected pivot IDs */
  public selectedPivotItems = signal<Set<string>>(new Set())

  /** Dialog & step state from URL */
  readonly showDialog = this.nav.showCaptureDialog
  readonly step = this.nav.pivotStep
  readonly chooseYourOwn = this.nav.chooseYourOwn

  readonly fields = signal<{
    primary_fields: string[]
    secondary_fields: string[]
  }>({
    primary_fields: [],
    secondary_fields: [],
  })

  primaryFields = signal<string[]>([])
  secondaryFields = signal<string[]>([])
  defaultFields: {
    primary_fields: string[]
    secondary_fields: string[]
  }

  /** Reactive form rebuilt whenever fields change */
  dataCaptureForm = signal<FormGroup>(this.buildForm(this.fields()))

  protected override initializeOptions(): void {
    this.options = { headerText: 'Apply Pivot Queries' }
  }

  constructor() {
    super()
    this.pivotAPIService
      .getFieldsForCategory(this.packageView.huntCategory())
      .subscribe((fields) => {
        this.defaultFields = fields
      })

    // Rebuild the form whenever `fields()` updates
    effect(() => {
      this.fields()
      this.refreshForm()
    })
  }

  /** Build a FormGroup from the fields definition */
  private buildForm(fieldsDef: {
    primary_fields: string[]
    secondary_fields: string[]
  }): FormGroup {
    const controls: Record<string, FormControl> = {}
    const { primary_fields: primaries, secondary_fields: secondaries } =
      fieldsDef
    primaries.forEach((field) => {
      controls[field] = new FormControl(
        '',
        this.chooseYourOwn() ? [Validators.required] : [],
      )
    })
    secondaries.forEach((field) => {
      controls[field] = new FormControl('')
    })
    return new FormGroup(controls)
  }

  private stateKey(): { huntId: string; queryId: string } | null {
    const huntId = this.session.activeHunt()?.id
    const baseQueryId = this.nav.selectedQueryId()
    if (!huntId || !baseQueryId) return null
    const pivotPath = this.nav.pivotPath()
    const queryId = pivotPath.length
      ? `${baseQueryId}:${pivotPath.join(':')}`
      : baseQueryId
    return { huntId, queryId }
  }

  /** Helper to reset the form whenever fields change */
  private refreshForm() {
    const fieldsDef = this.fields()
    this.primaryFields.set(fieldsDef.primary_fields)
    this.secondaryFields.set(fieldsDef.secondary_fields)

    this.formValuesSub?.unsubscribe()

    const form = this.buildForm(fieldsDef)
    const key = this.stateKey()
    if (key) {
      const cached = this.pivotFormState.getValues(key.huntId, key.queryId)
      form.patchValue(cached, { emitEvent: false })
      this.formValuesSub = form.valueChanges.subscribe((values) => {
        this.pivotFormState.saveValues(
          key.huntId,
          key.queryId,
          values as Record<string, string>,
        )
      })
    }

    this.dataCaptureForm.set(form)
  }

  ngOnDestroy(): void {
    this.formValuesSub?.unsubscribe()
  }

  /** Combined pivot items */
  readonly allItems = computed(() => [...this.autoItems()])

  /** Filtered by search term */
  readonly filteredItems = computed(() => {
    const q = this.pivotSearchTerm().toLowerCase().trim()
    return q
      ? this.allItems().filter(
          (item) =>
            item.title.toLowerCase().includes(q) ||
            item.description.toLowerCase().includes(q),
        )
      : this.allItems()
  })

  /** Pivot options */
  public autoItems = signal<PivotRecord[]>([])

  /** Breadcrumb steps */
  readonly breadcrumbs = this.breadcrumb.capturePivotSteps

  /** Navigate when a breadcrumb is clicked */
  onBreadcrumbClick(idx: number) {
    this.nav.mergeQueryParams({ step: this.breadcrumbs()[idx].step })
  }

  /** Fetch pivot matches and advance to pivot step */
  onContinue() {
    const filled = Object.entries(this.dataCaptureForm().value)
      .filter(([, v]) => v)
      .map(([k]) => k)

    this.pivotAPIService
      .getPivotMatches(this.queryView.selectedToolType(), filled)
      .subscribe({
        next: (items) => {
          this.autoItems.set(items)
          this.nav.mergeQueryParams({ step: 'pivot' })
        },
        error: (err) => {
          console.error(err)
          this.error.set('Failed to load pivot matches.')
        },
      })
  }

  /** Go back to data capture */
  goBack() {
    this.nav.mergeQueryParams({ step: 'capture' })
  }

  /** Go back to data capture */
  backToSelect() {
    this.selectedPivotItems.set(new Set())
    this.nav.clearQueryParams(['chooseYourOwn'])
    this.nav.mergeQueryParams({ step: 'select' })
  }

  /** Go back to pivot selection */
  backToPivotList() {
    this.nav.mergeQueryParams({ step: 'pivot' })
  }

  /** Go to data capture */
  chooseRecommendedPivots() {
    this.nav.mergeQueryParams({ chooseYourOwn: false, step: 'capture' })
    this.fields.set(this.defaultFields)
  }

  /** Go to pivot selection */
  chooseYourOwnPivots() {
    this.pivotAPIService
      .getAllPivotsForTool(this.queryView.selectedToolType())
      .subscribe({
        next: (items) => {
          this.autoItems.set(items)
          this.nav.mergeQueryParams({ chooseYourOwn: true, step: 'pivot' })
        },
        error: (err) => {
          console.error(err)
          this.error.set('Failed to load pivots.')
        },
      })
  }

  generateInputs() {
    const selected = this.allItems()
      .filter((i) => this.selectedPivotItems().has(i.UUID))
      .map((i) => i.UUID)

    this.pivotAPIService.getPivotFields(selected).subscribe({
      next: (items) => {
        this.fields.set({
          primary_fields: items.required,
          secondary_fields: items.optional,
        })
        this.nav.mergeQueryParams({ chooseYourOwn: true, step: 'capture' })
      },
      error: (err) => {
        console.error(err)
        this.error.set('Failed to load input fields.')
      },
    })
  }

  /** Toggle selection in the Set */
  public onPivotSelection(item: PivotRecord) {
    const current = new Set(this.selectedPivotItems())
    if (current.has(item.UUID)) current.delete(item.UUID)
    else current.add(item.UUID)
    this.selectedPivotItems.set(current)
  }

  /** Alias for template binding */
  public togglePivotSelection(item: PivotRecord) {
    this.onPivotSelection(item)
  }

  /** Submit selected pivots */
  onSubmit() {
    const selected = this.allItems().filter((i) =>
      this.selectedPivotItems().has(i.UUID),
    )
    const pivotFields = Object.fromEntries(
      Object.entries(this.dataCaptureForm().value)
        .filter(([, v]) => v)
        .map(([k, v]) => [k, String(v)]),
    )

    const calls = selected.map((p) =>
      this.pivotAPIService.getContentForPivot(
        p.UUID,
        this.nav.toolId(),
        pivotFields,
      ),
    )

    forkJoin(calls).subscribe({
      next: async (results: GeneratedContent[][]) => {
        const flat = results.flat()
        await this.packageEditor.appendPivotsToOriginalAnalysis(flat)
        this.queryEditor.appendPivots(flat)
        this.nav.mergeQueryParams({ showCaptureDialog: false, step: 'pivot' })
        this.dialogRef.close()
      },
      error: (err) => {
        console.error(err)
        this.error.set('Failed to generate pivot content. Please try again.')
      },
    })
  }

  /** Reset form and clear persisted values for this hunt+query */
  clearForm() {
    const key = this.stateKey()
    if (key) {
      this.pivotFormState.clearValues(key.huntId, key.queryId)
    }
    this.dataCaptureForm().reset()
  }

  /** Utility: humanize field name */
  toLabel(field: string): string {
    return field
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  /** TrackBy for ngFor */
  trackById(_: number, i: PivotRecord) {
    return i.UUID
  }
}
